import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getBill, getBillTalkingPoints } from "@/lib/api";
import { positionBadge, StageStepper } from "@/lib/bills";
import { ArrowLeft } from "lucide-react";
import { InlineAssistant } from "@/components/AssistantWidget";
import { useT } from "@/lib/t";
import { useLang } from "@/context/LangContext";

export default function BillDetail() {
  const { id } = useParams();
  const t = useT();
  const { lang } = useLang();
  const [bill, setBill] = useState(null);
  const [tp, setTp] = useState(null);

  useEffect(() => { getBill(id).then(setBill).catch(() => setBill(false)); }, [id]);
  useEffect(() => {
    getBillTalkingPoints(id).then(setTp).catch(() => setTp({ talking_points: [] }));
  }, [id]);

  if (bill === false) return <div className="max-w-4xl mx-auto px-6 py-24 font-serif text-2xl">{t("bills.bill_not_found")}</div>;
  if (!bill) return <div className="max-w-4xl mx-auto px-6 py-24">{t("common.loading")}</div>;

  const cstSuggestions = tp && tp.talking_points && tp.talking_points.length
    ? (lang === "es"
        ? [
            `Ofrezca un punto de Doctrina Social Católica sobre ${bill.number}.`,
            `¿Cuál es la posición de la Conferencia sobre ${bill.number} y por qué?`,
            `¿Cuándo es la próxima audiencia sobre ${bill.number}?`,
          ]
        : [
            `Draw a Catholic Social Teaching talking point for ${bill.number}.`,
            `What is the Conference's position on ${bill.number} and why?`,
            `When is the next hearing on ${bill.number}?`,
          ])
    : undefined;

  return (
    <div className="max-w-4xl mx-auto px-6 py-16" data-testid="bill-detail">
      <Link to="/legislative-work" className="text-ccc-plum inline-flex items-center gap-1 mb-8 underline decoration-ccc-gold underline-offset-4" style={{ fontSize: "15px" }}>
        <ArrowLeft size={14}/> {t("bills.back_to_tracker")}
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <span className={`px-2 py-1 uppercase tracking-wider font-semibold ${positionBadge(bill.position)}`} style={{ fontSize: "11px" }}>{bill.position}</span>
        <span className="font-mono text-ccc-muted" style={{ fontSize: "13px" }}>{bill.number} &middot; {bill.session}</span>
      </div>
      <h1 className="font-serif text-5xl text-ccc-ink leading-tight mb-6">{bill.title}</h1>
      <div className="mb-8"><StageStepper stage={bill.stage}/></div>
      <div className="gilded-divider mb-10"/>

      <div className="prose-ccc">
        <h2>{t("bills.why_it_matters")}</h2>
        <p>{bill.why_it_matters}</p>
        <h2>{t("bills.summary_label")}</h2>
        <p>{bill.summary}</p>
        {bill.committee && (
          <>
            <h2>{t("bills.committee_hearing")}</h2>
            <p>
              {bill.committee}
              {bill.hearing_date ? ` — ${t("bills.next_hearing")} ${bill.hearing_date}.` : ""}
            </p>
          </>
        )}
      </div>

      {tp && tp.talking_points && tp.talking_points.length > 0 && (
        <section className="mt-14" data-testid="bill-talking-points">
          <span className="gold-rule mb-4" aria-hidden="true"/>
          <div className="eyebrow mb-2">{t("bills.cst_eyebrow")}</div>
          <h2 className="font-serif font-bold text-ccc-ink mb-6" style={{ fontSize: "30px" }}>
            {t("bills.cst_heading")} {bill.number}.
          </h2>
          <div className="space-y-6">
            {tp.talking_points.map((tt) => (
              <article
                key={tt.slug}
                data-testid={`talking-point-${tt.slug}`}
                className="bg-ccc-white border-l-4 border-ccc-gold border-t border-r border-b border-ccc-rule p-6"
              >
                <div className="eyebrow mb-2" style={{ fontSize: "13px" }}>
                  {tt.principle}
                </div>
                <p className="text-ccc-body" style={{ fontSize: "18px", lineHeight: 1.7 }}>
                  {tt.point}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      <div className="mt-14 border border-ccc-rule bg-ccc-white p-6">
        <div className="eyebrow mb-2">{t("bills.take_action_eyebrow")}</div>
        <div className="font-serif font-semibold text-ccc-ink mb-4" style={{ fontSize: "22px" }}>
          {t("bills.make_voice_heard")} {bill.number}.
        </div>
        <Link
          to="/take-action/action-alerts"
          className="inline-block bg-ccc-plum text-ccc-paper px-6 py-3 uppercase tracking-[0.08em] font-semibold hover:bg-ccc-plum-deep"
          style={{ fontSize: "15px" }}
        >
          {t("bills.view_related_alerts")}
        </Link>
      </div>

      <div className="mt-14">
        <InlineAssistant
          tone="bill"
          context={`Bill ${bill.number}: ${bill.title}`}
          suggestions={cstSuggestions}
        />
      </div>
    </div>
  );
}
